/**
 * Cancel order-item — what the client posts to `POST /api/orders/cancel-item`
 * when a seller (or buyer, depending on order state) cancels one or more units
 * of an `order_items` row. The server validates the cancellable quantity
 * against the row's current `quantity` (net of prior cancels), then dispatches
 * a refund through the payment provider and writes the cancellation back to
 * the order.
 *
 * Why the schema is additive-only (every field but `orderItemId` optional):
 *
 *  - Web (`components/orders/OrderManagementCard.tsx`) sends ONLY
 *    `{ orderItemId, quantityToCancel }` — never `reason`.
 *  - Mobile (`apps/mobile/lib/api-orders.ts cancelOrderItem`) sends all three,
 *    but each as `T?`.
 *  - `quantityToCancel` is optional on the wire: when omitted the route
 *    defaults to "cancel all remaining units" (route's `requestedQtyToCancel`
 *    null-branch).
 *
 * The route still enforces the DB-state-dependent range check
 * (`qtyToCancel <= currentQty`) inline — that can't move into Zod because it
 * needs the row.
 *
 * @see app/api/orders/cancel-item/route.ts — handler
 * @see apps/mobile/lib/api-orders.ts cancelOrderItem — mobile caller
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface CancelItemRequest {
  /** `order_items.id` to cancel against. */
  orderItemId: string;
  /**
   * Units to cancel. Optional — omitted means "cancel all remaining units".
   * Must be a positive integer; the route additionally caps it at the row's
   * current (post-prior-cancels) `quantity`.
   */
  quantityToCancel?: number;
  /**
   * Cancellation note. Optional. Stored on `order_items.cancellation_reason`
   * and surfaced in the buyer/seller cancellation emails. Max 500 chars to
   * keep email templates predictable.
   */
  reason?: string;
}

// Runtime parse — Zod schema (boundary). `pinned<CancelItemRequest>()` is the
// compile-time schema↔interface pin; see `./pin.ts`.
export const CancelItemRequestSchema = pinned<CancelItemRequest>()(
  z.object({
    orderItemId: z.string().min(1),
    quantityToCancel: z.number().int().positive().optional(),
    reason: z.string().max(500).optional(),
  }),
);
